<template>
	<component
		:is="tag"
		:class="[
			$style.linearLayout,
			props.direction === 'vertical' ? $style.vertical : $style.horizontal,
			$style[`row-spacing-${parsedRowGap}`],
			$style[`column-spacing-${parsedColumnGap}`],
			$style[`align-${props.alignItems}`],
			$style[`justify-${props.justifyContent}`],
			$style[`margin-top-${props.marginTop}`],
			$style[`margin-bottom-${props.marginBottom}`],
			$style[`margin-right-${props.marginRight}`],
			$style[`margin-left-${props.marginLeft}`],
			$style[`padding-${props.padding}`],
			$style[`padding-top-${props.paddingTop}`],
			$style[`padding-bottom-${props.paddingBottom}`],
			$style[`padding-right-${props.paddingRight}`],
			$style[`padding-left-${props.paddingLeft}`],
			props.stretchChildren && $style.stretchChildren,
			props.direction === 'horizontal' && props.wrap && $style.wrap,
		]"
	>
		<slot />
	</component>
</template>
<script lang="ts" setup>
import type { Spacing, PaddingSpacing } from '../spacing';
import type { Align, Justify } from './common-linear-layout';

type Tag = keyof HTMLElementTagNameMap;
type Props = {
	/**
	 * Indicates the direction that the children should be laid out.
	 */
	direction?: 'horizontal' | 'vertical';
	/**
	 * Indicates the space between children.
	 */
	gap?: Spacing;
	rowGap?: Spacing;
	columnGap?: Spacing;
	/**
	 * Indicates if the content should be allowed to wrap.
	 */
	wrap?: boolean;
	/**
	 * Content alignment
	 */
	alignItems?: Align;
	justifyContent?: Justify;
	marginTop?: Spacing;
	marginBottom?: Spacing;
	marginLeft?: Spacing;
	marginRight?: Spacing;
	padding?: PaddingSpacing;
	paddingTop?: PaddingSpacing;
	paddingBottom?: PaddingSpacing;
	paddingLeft?: PaddingSpacing;
	paddingRight?: PaddingSpacing;
	/**
	 * Allows children to grow.
	 */
	stretchChildren?: boolean;

	tag?: Tag;
};

const props = withDefaults(defineProps<Props>(), {
	wrap: true,
	gap: 'm',
	rowGap: undefined,
	columnGap: undefined,
	direction: 'vertical',
	alignItems: undefined,
	justifyContent: undefined,
	marginTop: undefined,
	marginBottom: undefined,
	marginLeft: undefined,
	marginRight: undefined,
	padding: undefined,
	paddingTop: undefined,
	paddingBottom: undefined,
	paddingLeft: undefined,
	paddingRight: undefined,
	stretchChildren: false,
	tag: 'div',
});

const parsedRowGap = computed(() => {
	return props.rowGap || props.gap;
});

const parsedColumnGap = computed(() => {
	return props.columnGap || props.gap;
});
</script>
<style lang="scss" module>
@use '../common-mixins.scss' as commonMixins;

.linearLayout {
	--common-layout-row-gap: 0;
	--common-layout-column-gap: 0;
	display: flex;
	row-gap: var(--common-layout-row-gap);
	column-gap: var(--common-layout-column-gap);
}

.vertical {
	flex-direction: column;
}

.horizontal {
	flex-direction: row;
}

.wrap {
	flex-wrap: wrap;
}

.row-spacing-none {
	--common-layout-row-gap: 0;
}

.row-spacing-xxs {
	--common-layout-row-gap: var(--sizing--xxs);
}

.row-spacing-xs {
	--common-layout-row-gap: var(--sizing--xs);
}

.row-spacing-s {
	--common-layout-row-gap: var(--sizing--s);
}

.row-spacing-m {
	--common-layout-row-gap: var(--sizing--m);
}

.row-spacing-l {
	--common-layout-row-gap: var(--sizing--l);
}

.row-spacing-xl {
	--common-layout-row-gap: var(--sizing--xl);
}

.row-spacing-xxl {
	--common-layout-row-gap: var(--sizing--xxl);
}

.column-spacing-none {
	--common-layout-column-gap: 0;
}

.column-spacing-xxs {
	--common-layout-column-gap: var(--sizing--xxs);
}

.column-spacing-xs {
	--common-layout-column-gap: var(--sizing--xs);
}

.column-spacing-s {
	--common-layout-column-gap: var(--sizing--s);
}

.column-spacing-m {
	--common-layout-column-gap: var(--sizing--m);
}

.column-spacing-l {
	--common-layout-column-gap: var(--sizing--l);
}

.column-spacing-xl {
	--common-layout-column-gap: var(--sizing--xl);
}

.column-spacing-xxl {
	--common-layout-column-gap: var(--sizing--xxl);
}

.align-start {
	align-items: flex-start;
}
.align-end {
	align-items: flex-end;
}
.align-center {
	align-items: center;
}

.justify-start {
	justify-content: start;
}
.justify-end {
	justify-content: end;
}
.justify-center {
	justify-content: center;
}
.justify-space-between {
	justify-content: space-between;
}
.justify-space-around {
	justify-content: space-around;
}
.justify-space-evenly {
	justify-content: space-evenly;
}

.stretchChildren {
	> * {
		flex-grow: 1;
	}
}

@include commonMixins.margin('top');
@include commonMixins.margin('bottom');
@include commonMixins.margin('left');
@include commonMixins.margin('right');

@include commonMixins.padding();
@include commonMixins.padding('top');
@include commonMixins.padding('bottom');
@include commonMixins.padding('left');
@include commonMixins.padding('right');
</style>
